import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { allapotCimke, elteresCimke, rangCimke, valaszCimke } from '../minoseg/cimkek.ts';
import { formatDatum, formatDatumIdo } from '../lib/formaz.ts';
import { supabase } from '../lib/supabase.ts';
import { HibaUzenet, Kartya } from '../ui/elemek.tsx';

function Sor({ cimke, ertek }: { cimke: string; ertek: string }) {
  return (
    <div className="grid grid-cols-[minmax(0,1fr)] gap-0.5 py-2 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] sm:gap-3">
      <dt className="text-sm text-szoveg-halk">{cimke}</dt>
      <dd className="wrap-anywhere font-medium">{ertek}</dd>
    </div>
  );
}

/**
 * A lead látogatói visszajelzése (minőségellenőrzés). Csak super adminnak jelenik meg (a hívó is ellenőrzi, és az RLS
 * is csak az aal2-es super adminnak ad sort), és csak ha a leadhez ment ki kérdőív. Az iroda ezt soha nem látja.
 * A tokent és az e-mail-hash-t nem kérjük le.
 */
export default function MinosegPanel({ leadId }: { leadId: string }) {
  const lekeres = useQuery({
    queryKey: ['minoseg-panel', leadId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('minoseg_kerdoivek')
        .select('id, kuldve_at, ervenyes_ig, valaszolt_at, tiltakozott_at, kapcsolat, szemle, megbizas, elegedettseg, megjegyzes, elteresek, elteres_allapot, elteres_megjegyzes, crm_statusz_kuldeskor, crm_elert_rang_kuldeskor')
        .eq('lead_id', leadId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  if (lekeres.error) return <HibaUzenet hiba={lekeres.error} />;
  const k = lekeres.data;
  if (!k) return null;

  return (
    <Kartya>
      <h2 className="mb-1 text-[17px] font-medium tracking-[-0.015em]">Visszajelzés</h2>
      <p className="text-sm text-szoveg-halk">A látogató válasza a minőségellenőrző kérdőívre. Az iroda ezt nem látja.</p>
      <dl className="mt-2 divide-y divide-vonal">
        <Sor cimke="Kérdőív kiküldve" ertek={formatDatum(k.kuldve_at)} />
        <Sor cimke="Kiküldéskor a CRM-ben" ertek={rangCimke(k.crm_elert_rang_kuldeskor)} />
        {k.tiltakozott_at ? (
          <Sor cimke="Tiltakozás" ertek={`Nem kért ilyen levelet (${formatDatumIdo(k.tiltakozott_at)}); a válaszai törölve.`} />
        ) : k.valaszolt_at ? (
          <>
            <Sor cimke="Válaszolt" ertek={formatDatumIdo(k.valaszolt_at)} />
            <Sor cimke="Felvették vele a kapcsolatot?" ertek={valaszCimke('kapcsolat', k.kapcsolat)} />
            <Sor cimke="Volt szemle?" ertek={valaszCimke('szemle', k.szemle)} />
            <Sor cimke="Kötött megbízást?" ertek={valaszCimke('megbizas', k.megbizas)} />
            <Sor cimke="Elégedettség" ertek={k.elegedettseg === null ? '–' : `${k.elegedettseg} / 5`} />
            {k.megjegyzes && <Sor cimke="Megjegyzés" ertek={k.megjegyzes} />}
          </>
        ) : (
          <Sor cimke="Válasz" ertek={`Még nem válaszolt (${formatDatum(k.ervenyes_ig)}-ig tehet).`} />
        )}
        {k.elteresek.length > 0 && (
          <>
            <Sor cimke="Eltérés a CRM-től" ertek={k.elteresek.map(elteresCimke).join('; ')} />
            <Sor cimke="Eltérés állapota" ertek={`${allapotCimke(k.elteres_allapot)}${k.elteres_megjegyzes ? `: ${k.elteres_megjegyzes}` : ''}`} />
          </>
        )}
      </dl>
      {k.elteresek.length > 0 && (
        <Link to={`/super/minoseg?lead=${leadId}`} className="mt-2 inline-flex min-h-10 items-center text-sm text-kiemelt underline">Megnyitás a Minőség oldalon</Link>
      )}
    </Kartya>
  );
}
